import React, { useState, useEffect } from 'react';
import { isAuditorAvailableForSlot } from '../utils/sessionUtils';
import * as mentorApi from '../api/mentorApi';

export default function ManageAssignmentsModal({
  session,
  counselors = [],
  auditors = [],
  teachers = [],
  cohorts = [],
  onClose,
  onSaved,
  onRefreshAuditors,
  showToast,
}) {
  const cohort = cohorts.find(c => c.id === session.cohortId);
  const resolvedSpocId = session.spocId || cohort?.spocId || '';

  const [counselorId, setCounselorId] = useState(session.counselorId || '');
  const [spocId, setSpocId] = useState(resolvedSpocId);
  const [selectedAuditorIds, setSelectedAuditorIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [roster, setRoster] = useState(auditors);

  useEffect(() => {
    setRoster(auditors);
  }, [auditors]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const raw = await mentorApi.getAuditors();
        if (!cancelled) setRoster(mentorApi.normalizeAuditors(raw));
      } catch (err) {
        console.error('Failed to refresh auditors on assignment open:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [session.id]);

  // Pre-populate auditor IDs from session's auditorAssignments
  useEffect(() => {
    const currentAuditorIds = (session.auditorAssignments || [])
      .filter(a => a.assignmentStatus === 'pending' || a.assignmentStatus === 'accepted')
      .map(a => String(a.auditorId || a.user_id))
      .filter(Boolean);
    setSelectedAuditorIds(currentAuditorIds);
  }, [session.auditorAssignments]);

  const toggleAuditor = (audId) => {
    setSelectedAuditorIds(prev => {
      const strId = String(audId);
      return prev.includes(strId)
        ? prev.filter(id => id !== strId)
        : [...prev, strId];
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const apiPayload = {
        counselor_id: counselorId ? Number(counselorId) : null,
        spoc_id: spocId ? Number(spocId) : null,
        auditor_assignments: selectedAuditorIds.map((auditorId, index) => ({
          user_id: Number(auditorId),
          breakout_room_number: index + 1,
        })),
      };
      await mentorApi.updateSession(session.id, apiPayload);
      if (onSaved) onSaved();
      if (showToast) showToast('Session assignments updated.');
      onClose();
    } catch (err) {
      console.error('Failed to update assignments:', err);
      if (showToast) showToast(err?.message || 'Failed to update assignments.');
    } finally {
      setSaving(false);
    }
  };

  const isPending = (session.auditors || []).length === 0 && !session.counselorId;

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.4)', zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          background: '#fff', borderRadius: '12px', padding: '28px', maxWidth: '600px',
          width: '90%', maxHeight: '85vh', overflowY: 'auto',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>
            Manage Assignments
          </h2>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: 'var(--text-muted)', padding: '4px' }}
          >
            ✕
          </button>
        </div>

        <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '20px' }}>
          {session.sessionType} · {session.cohortName || session.groupName}
          {isPending && (
            <span style={{ marginLeft: '8px', fontSize: '11px', background: '#fef3c7', color: '#b45309', padding: '2px 8px', borderRadius: '999px', fontWeight: 600 }}>
              Needs Assignment
            </span>
          )}
        </p>

        {/* SPOC selector */}
        <div className="form-field-group" style={{ marginBottom: '20px' }}>
          <label style={{ fontWeight: 600, fontSize: '13px', marginBottom: '6px', display: 'block' }}>
            SPOC (Override)
          </label>
          <select
            className="form-control-select"
            value={spocId}
            onChange={e => setSpocId(e.target.value)}
          >
            <option value="">-- Cohort Default ({cohort?.spocName || 'None'}) --</option>
            {(teachers || []).map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
          <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: '4px 0 0', fontStyle: 'italic' }}>
            Leave as "Cohort Default" to inherit the cohort's SPOC. Select a specific teacher to override per-session.
          </p>
        </div>

        {/* Counselor selector — single-select */}
        <div className="form-field-group" style={{ marginBottom: '20px' }}>
          <label style={{ fontWeight: 600, fontSize: '13px', marginBottom: '6px', display: 'block' }}>
            Counselor (Optional)
          </label>
          <select
            className="form-control-select"
            value={counselorId}
            onChange={e => setCounselorId(e.target.value)}
          >
            <option value="">-- None (No Counselor) --</option>
            {(counselors || []).map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: '4px 0 0', fontStyle: 'italic' }}>
            All active counselors shown. Single-select — one counselor per session.
          </p>
        </div>

        {/* Auditors section */}
        <div className="form-field-group" style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <label style={{ fontWeight: 600, fontSize: '13px', display: 'block' }}>
              Auditors
            </label>
            <button
              type="button"
              className="btn-secondary-action"
              style={{ fontSize: '12px', padding: '4px 10px' }}
              disabled={refreshing}
              onClick={async () => {
                setRefreshing(true);
                try {
                  if (onRefreshAuditors) await onRefreshAuditors();
                  const raw = await mentorApi.getAuditors();
                  setRoster(mentorApi.normalizeAuditors(raw));
                  if (showToast) showToast('Auditor availability refreshed.');
                } catch (err) {
                  if (showToast) showToast(err?.message || 'Failed to refresh auditors.');
                } finally {
                  setRefreshing(false);
                }
              }}
            >
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
          {roster.length === 0 ? (
            <div style={{ color: 'var(--text-muted)', fontSize: '13px', fontStyle: 'italic', padding: '12px', background: 'var(--neutral-50)', borderRadius: '8px' }}>
              No auditors available.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '300px', overflowY: 'auto' }}>
              {roster.map(aud => {
                const strId = String(aud.id);
                const isSelected = selectedAuditorIds.includes(strId);
                const slot = session.timeSlot || '';
                const isAvailable = slot
                  ? isAuditorAvailableForSlot(aud.availabilitySlots, slot, session.scheduledAt || null)
                  : true;
                return (
                  <div
                    key={aud.id}
                    style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '10px 12px', borderRadius: '8px',
                      border: `1px solid ${isSelected ? 'var(--primary-navy)' : 'var(--border-color)'}`,
                      background: isSelected ? 'rgba(1,71,162,0.04)' : '#fff',
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 500, fontSize: '14px', color: 'var(--neutral-800)' }}>{aud.name}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{aud.email}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {!isAvailable && (
                        <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontStyle: 'italic' }}>Unavailable</span>
                      )}
                      <button
                        type="button"
                        className={`btn-assign-auditor ${isSelected ? 'remove' : 'assign'}`}
                        onClick={() => toggleAuditor(aud.id)}
                        style={{
                          padding: '6px 14px', borderRadius: '4px', fontWeight: 600,
                          fontSize: '12px', cursor: 'pointer',
                        }}
                      >
                        {isSelected ? 'Remove' : 'Assign'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', borderTop: '1px solid var(--border-color)', paddingTop: '16px' }}>
          <button className="btn-secondary-action" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="btn-submit-session" onClick={handleSave} disabled={saving} style={{ padding: '10px 24px' }}>
            {saving ? 'Saving…' : 'Save Assignments'}
          </button>
        </div>
      </div>
    </div>
  );
}
